import {
	type ApplicationError,
	type ForManagingTeamMembers,
	type TeamMember
} from 'core'
import { z } from 'zod'

import { HTTPClient, HTTPMethods } from './http_client'
import type { TeamMemberDTO } from './types'

export class TeamMembersManager implements ForManagingTeamMembers {
	private readonly client = new HTTPClient()

	private formatMember(member: TeamMemberDTO): TeamMember {
		return {
			id: member.id,
			teamId: member.team_id,
			userId: member.user.id,
			role: member.role
		}
	}

	async addMember(
		params: z.infer<typeof ForManagingTeamMembers.AddTeamMember>
	): Promise<{
		data?: TeamMember
		error?: ApplicationError
	}> {
		const response = await this.client.fetch<
			TeamMemberDTO,
			{
				team_id: string
				user_id: string
				role: string
			}
		>('/authv2/teams/api/v1/members', {
			method: HTTPMethods.Post,
			data: {
				team_id: params.teamId,
				user_id: params.email,
				role: params.role
			}
		})

		if (response.error) {
			return { error: response.error }
		}

		return {
			data: response.data ? this.formatMember(response.data) : undefined
		}
	}

	async removeMember(
		teamId: string,
		userId: string
	): Promise<{
		error?: ApplicationError
	}> {
		const response = await this.client.fetch(
			`/authv2/teams/api/v1/members/${teamId}/${userId}`,
			{
				method: HTTPMethods.Delete
			}
		)

		if (response.error) {
			return { error: response.error }
		}

		return {}
	}

	async updateMemberRole(
		params: z.infer<typeof ForManagingTeamMembers.AddTeamMember>
	): Promise<{
		data?: TeamMember
		error?: ApplicationError
	}> {
		const response = await this.client.fetch<
			TeamMemberDTO,
			{
				role: string
			}
		>(`/authv2/teams/api/v1/members/${params.teamId}/${params.email}`, {
			method: HTTPMethods.Put,
			data: {
				role: params.role
			}
		})

		if (response.error) {
			return { error: response.error }
		}

		return {
			data: response.data ? this.formatMember(response.data) : undefined
		}
	}

	async fetchMembers(teamId: string): Promise<{
		data?: TeamMember[]
		error?: ApplicationError
	}> {
		const response = await this.client.fetch<{
			members: TeamMemberDTO[]
		}>(`/authv2/teams/api/v1/members/${teamId}`)

		if (response.error) {
			return { error: response.error }
		}

		return {
			data: response.data?.members.map(this.formatMember) || []
		}
	}
}

export class TeamMembersManagerMock implements ForManagingTeamMembers {
	data?: TeamMember
	error?: ApplicationError

	async addMember(): Promise<{
		data?: TeamMember
		error?: ApplicationError
	}> {
		return {
			data: this.data,
			error: this.error
		}
	}

	async removeMember(): Promise<{
		error?: ApplicationError
	}> {
		return {
			error: this.error
		}
	}

	async updateMemberRole(): Promise<{
		data?: TeamMember
		error?: ApplicationError
	}> {
		return {
			data: this.data,
			error: this.error
		}
	}

	async fetchMembers(): Promise<{
		data?: TeamMember[]
		error?: ApplicationError
	}> {
		return {
			data: this.data ? [this.data] : [],
			error: this.error
		}
	}
}
